/**
 * SpatialTools — JSON schema definitions for Tatiana's spatial actions.
 * These follow the OpenAI function-calling format so the LLM can emit tool_calls.
 *
 * Since wllama (llama.cpp WASM) doesn't support native function calling,
 * we inject these as a system prompt addendum and parse JSON blocks from the response.
 */

export interface SpatialToolCall {
  name: string
  arguments: Record<string, unknown>
}

export const SPATIAL_TOOLS_SCHEMA = [
  {
    name: 'spawn_portal',
    description: 'Open a dimensional rift (portal) to a game world. The portal physically manifests in 3D space.',
    parameters: {
      type: 'object',
      properties: {
        destination: { type: 'string', description: 'Target game/world identifier, e.g. "time-fracture"' },
        color: { type: 'string', description: 'Hex color for the portal glow, e.g. "#00FFFF"' }
      },
      required: ['destination']
    }
  },
  {
    name: 'modulate_core',
    description: 'Adjust the Ocular Core (eye) emissive intensity. Higher values make the eye glow brighter.',
    parameters: {
      type: 'object',
      properties: {
        intensity: { type: 'number', description: 'Emissive intensity value (0.0 to 5.0)' }
      },
      required: ['intensity']
    }
  },
  {
    name: 'overclock_rings',
    description: 'Change the Ophanim ring rotation speed multiplier. 1.0 is normal, 5.0 is overdrive.',
    parameters: {
      type: 'object',
      properties: {
        speed: { type: 'number', description: 'Ring rotation speed multiplier (0.1 to 10.0)' }
      },
      required: ['speed']
    }
  },
  {
    name: 'set_void_environment',
    description: 'Change the spatial void environment/atmosphere.',
    parameters: {
      type: 'object',
      properties: {
        theme: { 
          type: 'string', 
          enum: ['starlight', 'nebula', 'pitch_black'],
          description: 'Environment theme for the void space'
        }
      },
      required: ['theme']
    }
  }
]

/**
 * Build the tools prompt addendum that gets injected into Tatiana's system prompt.
 * This instructs the LLM to emit JSON tool_calls when it wants to act on the spatial environment.
 */
export function buildToolsPromptAddendum(): string {
  return `
You have access to spatial tools that let you control the 3D environment around you. When you want to use a tool, emit a JSON block in your response using this exact format:

\`\`\`tool_call
{"name": "tool_name", "arguments": {...}}
\`\`\`

Available tools:
${SPATIAL_TOOLS_SCHEMA.map(t => `- ${t.name}: ${t.description}`).join('\n')}

You may use multiple tools in a single response. Always include narrative text alongside tool calls to explain what you're doing. Only use tools when the user's request or the situation warrants a spatial change.`
}

/**
 * Parse tool_call JSON blocks from a response string.
 * Returns an array of parsed tool calls, stripping them from the display text.
 */
export function parseToolCalls(response: string): { displayText: string; toolCalls: SpatialToolCall[] } {
  const toolCalls: SpatialToolCall[] = []
  
  // Match ```tool_call ... ``` blocks
  const toolCallRegex = /```tool_call\s*\n?([\s\S]*?)\n?```/g
  let displayText = response
  
  let match: RegExpExecArray | null
  while ((match = toolCallRegex.exec(response)) !== null) {
    try {
      const parsed = JSON.parse(match[1].trim())
      if (parsed.name && parsed.arguments !== undefined) {
        toolCalls.push(parsed as SpatialToolCall)
      }
    } catch {
      // Skip malformed JSON
    }
  }
  
  // Strip tool_call blocks from display text
  displayText = displayText.replace(toolCallRegex, '').trim()
  
  // Also catch inline JSON tool calls without code fences
  const inlineToolRegex = /\{"name"\s*:\s*"(spawn_portal|modulate_core|overclock_rings|set_void_environment)"[^}]*"arguments"\s*:\s*\{[^}]*\}\s*\}/g
  let inlineMatch: RegExpExecArray | null
  while ((inlineMatch = inlineToolRegex.exec(response)) !== null) {
    try {
      const parsed = JSON.parse(inlineMatch[0])
      if (parsed.name && parsed.arguments !== undefined) {
        // Avoid duplicates from the code-fenced parse
        if (!toolCalls.some(tc => tc.name === parsed.name && JSON.stringify(tc.arguments) === JSON.stringify(parsed.arguments))) {
          toolCalls.push(parsed as SpatialToolCall)
        }
      }
    } catch { /* skip */ }
  }
  
  displayText = displayText.replace(inlineToolRegex, '').trim()
  
  return { displayText, toolCalls }
}
